/* eslint-disable jsx-a11y/anchor-is-valid */
/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import { Layout } from '@components/common'
import type {
    GetStaticProps,
    GetStaticPaths,
    GetStaticPropsContext,
    InferGetStaticPropsType
} from 'next'
import { useUI } from '@corratech/pylot-ui/context'
import { Component404, NotFound } from '@components/404'
import getCategory, {
    getCategoryIdentifiers
} from '@pylot-data/api/operations/get-category'
import { Breadcrumbs, CategoryBreadcrumb } from '@corratech/pylot-breadcrumbs'
import { RecentlyViewed } from '@corratech/pylot-recently-viewed'
import { useCategory } from '@pylot-data/hooks/category/use-category'
import useIsPWA from '@lib/hooks/useIsPWA'
import { useLoadMoreButton } from '@lib/hooks/useLoadMoreButton'
import cn from 'classnames'
import { useTranslation } from 'next-i18next/pages'
import { serverSideTranslations } from 'next-i18next/pages/serverSideTranslations'
import { Filters, usePLPManager } from '@corratech/pylot-filters-and-sort'
import { usePlpProducts } from '@pylot-data/hooks/category/use-plp-products'
import { Toolbar } from '@components/corra/ProductListToolbar'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { SEO, PAGE_TYPE } from '@corratech/pylot-seo'
import s from '@pagestyles/Plp.module.scss'
import { getSubcategories } from '@pylot-data/api/operations/get-subcategories'
import { getStoreConfig } from 'config'
import { useUrlResolver } from '@pylot-data/hooks/urlResolver/use-url-resolver'
import { useRouter } from 'next/router'
import { RichContent } from '@corratech/pylot-rich-content'
import getCategoriesSlider from '@pylot-data/api/operations/get-categories-slider'
import { viewCategoryEvent } from '@lib/utils/exponeaTrackingEvents'
import useCarousel from '@pylot-data/hooks/cms/use-carousel'
import { useIsMobileScreen } from '@lib/utils/mediaQuery'
import { TAB_CONTAINER_SELECTOR, TAB_ITEMS_SELECTOR } from '@lib/utils/cms'
import useTabsControl from '@pylot-data/hooks/cms/use-tabs-control'
import { optimizeImages } from '@corratech/pylot-seo/src/util/imageOptimizationUtils'

import { PLP_CONFIG, SPECIAL_CATEGORIES } from '@pylot-data/constants/plp'
import { usePLPState } from '@lib/hooks/cagegory/plp/usePLPState'
import { usePLPScrolling } from '@lib/hooks/cagegory/plp/usePLPScrolling'
import { usePLPGridColumns } from '@lib/hooks/cagegory/plp/usePLPGridColumns'
import { usePLPAnalytics } from '@lib/hooks/cagegory/plp/usePLPAnalytics'
import {
    usePLPScrollRestore,
    usePLPScrollRestoreEntry
} from '@lib/hooks/cagegory/plp/usePLPScrollRestore'
import {
    decodeCategoryId,
    getDefaultSort,
    getSortLabel,
    getPreParsedPageBuilderData,
    shouldDisplayLoading as shouldDisplayLoadingHelper,
    shouldUsePreParsedPageBuilder,
    calculateCategoryMetadata
} from '@lib/utils/plpHelpers'
import { shouldPrerenderPaths } from '@lib/utils/staticPaths'
import { CategorySlider } from '@components/Category/plp/CategorySlider'
import { PLPProductGrid } from '@components/Category/plp/ProductGrid'
import { WrappedProductItem } from '@components/Category/plp/WrappedProductItem'
import { ProductInterface } from '@pylot/pylotschema'

const GIFT_CARD_PLP = SPECIAL_CATEGORIES.GIFT_CARD
const GIFTCARD_PLP = SPECIAL_CATEGORIES.GIFTCARD

export const getStaticPaths: GetStaticPaths = async () => {
    if (!shouldPrerenderPaths()) {
        return { paths: [], fallback: 'blocking' }
    }

    const config = getStoreConfig()
    const isUAT = Number(process.env.NEXT_PUBLIC_UAT_DEVELOP) == 1

    if (isUAT) {
        return {
            paths: config.static.categories?.root
                ? (
                      await getSubcategories({
                          rootCategory: config.static.categories.root,
                          pageSize: config.static.categories.count,
                          currentPage: 1
                      })
                  )
                      .slice(0, 1)
                      .map((category) => `/categoria/${category.url_path}`)
                : [],
            fallback: 'blocking'
        }
    }
    return {
        paths: config.static.categories?.root
            ? (
                  await getSubcategories({
                      rootCategory: config.static.categories.root,
                      pageSize: config.static.categories.count,
                      currentPage: 1
                  })
              ).map((category) => `/categoria/${category.url_path}`)
            : [],
        fallback: 'blocking'
    }
}

export const getStaticProps: GetStaticProps = async ({
    params,
    locale
}: GetStaticPropsContext) => {
    const urlPathParts = params!.url_path as string[]
    const url_path = urlPathParts.join('/')

    try {
        // El slider solo necesita el id numérico, así que se resuelve junto al
        // uid y se pide en paralelo con la categoría en vez de encadenarlo
        // detrás de ella. (Magento 2.4.8 removió CategoryTree.id: el helper
        // deriva el id numérico del uid.)
        const { uid, id } = await getCategoryIdentifiers({ url_path, locale })

        if (!uid) {
            // revalidate corto: un fallo transitorio del backend no debe dejar
            // el 404 cacheado indefinidamente.
            return { notFound: true, revalidate: 60 }
        }

        const [categoryData, initialSliderData] = await Promise.all([
            getCategory({
                pageSize: PLP_CONFIG.PAGE_SIZE,
                url_path,
                locale,
                categoryUid: uid
            }),
            id ? getCategoriesSlider({ categoryId: Number(id) }) : []
        ])

        if (!categoryData) {
            return { notFound: true, revalidate: 60 }
        }

        const category = categoryData?.categories?.items?.[0] ?? null
        const categoryUid = category?.uid ?? ''

        const categoryDescription = category?.description ?? ''
        const categorySeoDescription = category?.category_seo_description ?? ''

        return {
            props: {
                url_path,
                categoryData: categoryData ?? null,
                categoryUid,
                initialSliderData,
                categoryDescriptionPageBuilderData:
                    getPreParsedPageBuilderData(categoryDescription),
                categorySeoFooterPageBuilderData: getPreParsedPageBuilderData(
                    categorySeoDescription
                ),
                ...(await serverSideTranslations(locale!, [
                    'common',
                    'plp',
                    'wishlist'
                ]))
            },
            revalidate: PLP_CONFIG.REVALIDATE_TIME // Updated
        }
    } catch (error) {
        // Si el backend falla durante el prerender/ISR, no propagar el error
        // (rompería la página con un 500): servir el shell sin data inicial para
        // que el cliente la cargue dinámicamente (useCategory revalida al montar)
        // y reintentar el SSR pronto vía revalidate corto.
        console.error(
            `PLP getStaticProps failed for "${url_path}"; serving shell for client-side fetch`,
            error
        )
        return {
            props: {
                url_path,
                categoryData: null,
                categoryUid: '',
                initialSliderData: [],
                categoryDescriptionPageBuilderData: null,
                categorySeoFooterPageBuilderData: null,
                ...(await serverSideTranslations(locale!, [
                    'common',
                    'plp',
                    'wishlist'
                ]))
            },
            revalidate: 60
        }
    }
}

export default function PLP({
    categoryData: categoryInitialData,
    categoryUid,
    url_path,
    Store,
    initialSliderData,
    categoryDescriptionPageBuilderData,
    categorySeoFooterPageBuilderData
}: InferGetStaticPropsType<typeof getStaticProps>): JSX.Element {
    const { t } = useTranslation(['common', 'plp', 'wishlist'])
    const { LoadingIndicator } = useUI()
    const config = getStoreConfig()

    const router = useRouter()
    const { filter: filterParam, isHorizontal } = router.query
    const isHorizontalFilterMenu = isHorizontal !== 'false'

    const { activeLabel, setActiveLabel, isSortVisible, setIsSortVisible } =
        usePLPState(categoryUid)

    const [sliderData, setSliderData] = useState(initialSliderData)
    const isMobile = useIsMobileScreen()
    const isPWA = useIsPWA()
    const parsedPath = router.asPath.slice(1)
    useUrlResolver(parsedPath)
    const urlPath = url_path || router.asPath?.split('/categoria/')?.[1]
    // Magento 2.4.8 removió CategoryTree.id; derivamos el id numérico del uid.
    const initialCategory = categoryInitialData?.categories?.items?.[0]
    const categoryUidValue = initialCategory?.uid
    const categoryId = decodeCategoryId(categoryUidValue)
    const initialCategoryDescription = initialCategory?.description ?? ''
    const initialCategorySeoDescription =
        initialCategory?.category_seo_description ?? ''
    const filtersRef = useRef<HTMLDivElement>(null)

    // REPLACE old scroll logic with a custom hook usePLPScrolling
    const { isHidden } = usePLPScrolling(filtersRef)

    // Posición guardada al salir hacia la PDP (solo si volvemos a esta misma
    // entrada de historial): rehidrata las páginas cargadas y vuelve al SKU.
    const { entry: scrollRestoreEntry, restoreSize } =
        usePLPScrollRestoreEntry(2)
    const { gridColumns, setGridColumns, isGridReflowing } = usePLPGridColumns()

    // Keep the existing category and product logic...
    const { productsResult: unfilteredProductResult } = useCategory(
        urlPath,
        categoryUid,
        {
            pageSize: PLP_CONFIG.PAGE_SIZE, // Updated
            filter: {},
            sort: {},
            searchCriteria: []
        },
        {
            initialData: {
                data: { categories: categoryInitialData?.categories }
            },
            revalidateOnMount: true,
            revalidateOnFocus: false
        },
        {
            initialData: [
                { data: { products: categoryInitialData?.products } }
            ],
            revalidateOnMount: true,
            revalidateOnFocus: false,
            initialSize: 2
        },
        { headers: { Store } },
        !filterParam || filterParam.length === 0,
        PLP_CONFIG.REQUESTS_PAGE_SIZE // Updated
    )

    const plpManager = usePLPManager({
        categoryUid,
        unfilteredAggregations: unfilteredProductResult?.aggregations
    })

    // ADD memoized check for gift card categories
    const isGiftCardCategory = useMemo(
        () => urlPath === GIFT_CARD_PLP || urlPath === GIFTCARD_PLP,
        [urlPath]
    )

    useEffect(() => {
        return () => plpManager.clearPLPState({})
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [categoryUid])

    // REPLACE old sort logic with helper function
    useEffect(() => {
        if (Object.keys(plpManager.plpState.sort).length === 0) {
            const defaultSortBy =
                categoryInitialData?.categories?.items?.[0]?.default_sort_by
            const { sort, label } = getDefaultSort(defaultSortBy)
            plpManager.plpState.sort = sort
            setActiveLabel(label)
        } else {
            // Orden restaurado desde la URL (volver atrás / recarga): la etiqueta
            // del toolbar debe reflejarlo. Si el valor no es conocido se conserva
            // la etiqueta que eligió el usuario.
            setActiveLabel((current) =>
                getSortLabel(plpManager.plpState.sort, current)
            )
        }
    }, [categoryInitialData, plpManager.plpState.sort, setActiveLabel])

    const {
        category,
        productItems,
        productsResult,
        error,
        categoriesSwrResult: { isValidating: isCategoryValidating },
        productsSwrResult: {
            setSize,
            size: productsPageCount,
            mutate: revalidateProducts,
            isValidating: isProductsValidating,
            error: productsError
        },
        isLast,
        isLoading: isCategoryDataLoading,
        hasPageError,
        currentPageNumber
    } = useCategory(
        urlPath,
        categoryUid,
        {
            pageSize: PLP_CONFIG.PAGE_SIZE, // Updated
            filter: plpManager.plpState.filters,
            sort: plpManager.plpState.sort,
            searchCriteria: plpManager.plpState.appliedSearchCritieria
        },
        {
            initialData: {
                data: { categories: categoryInitialData?.categories }
            },
            revalidateOnMount: true,
            revalidateOnFocus: false
        },
        {
            initialData: [
                { data: { products: categoryInitialData?.products } }
            ],
            revalidateOnMount: true,
            revalidateOnFocus: false,
            initialSize: 2,
            restoreSize
        },
        { headers: { Store } },
        false,
        PLP_CONFIG.REQUESTS_PAGE_SIZE // Updated
    )

    useEffect(() => {
        if (category?.name && window) {
            window.sessionStorage.setItem('origin-category', category?.name)
            window.sessionStorage.setItem(
                'origin-category-path',
                category?.url_path ?? ''
            )
            // Filtros y orden activos: el breadcrumb de la PDP los conserva al
            // volver a esta categoría.
            window.sessionStorage.setItem(
                'origin-category-search',
                window.location.search
            )
        }
    }, [category?.name, router.asPath])

    useEffect(() => {
        const fetchSliderData = async () => {
            if (categoryId) {
                try {
                    const updatedSliderData = await getCategoriesSlider({
                        categoryId: Number(categoryId)
                    })
                    if (updatedSliderData) setSliderData(updatedSliderData)
                } catch (e) {
                    console.error('Error fetching slider data:', e)
                }
            }
        }
        fetchSliderData()
    }, [categoryId])

    useEffect(() => {
        viewCategoryEvent(category)
    }, [urlPath])

    const { onLoadMoreClick } = useLoadMoreButton(
        isCategoryDataLoading || isProductsValidating,
        setSize
    )

    const { visibleProductData, productsToRender, marketingTiles } =
        usePlpProducts(
            isCategoryDataLoading,
            plpManager,
            productItems,
            categoryInitialData?.products,
            productsResult,
            category?.parsedInterleavedContent
        )

    // REPLACE manual calculation with helper
    const { totalProducts, renderedCount, amountBarWidth } = useMemo(
        () =>
            calculateCategoryMetadata(
                productsResult,
                categoryInitialData,
                productsToRender
            ),
        [productsResult, categoryInitialData, productsToRender]
    )

    const toolbar = useMemo(
        () => (
            <div className={s['mobile-filters']}>
                <Toolbar
                    sortFields={categoryInitialData?.products?.sort_fields}
                    plpManager={plpManager}
                    activeLabel={activeLabel}
                    setActiveLabel={setActiveLabel}
                    isSortVisible={isSortVisible}
                    setIsSortVisible={setIsSortVisible}
                    gridColumns={gridColumns}
                    setGridColumns={setGridColumns}
                />
            </div>
        ),
        [
            activeLabel,
            categoryInitialData?.products?.sort_fields,
            isSortVisible,
            plpManager,
            gridColumns,
            setActiveLabel,
            setIsSortVisible,
            setGridColumns
        ]
    )

    const productsToRenderWithMarketingTile = useMemo(() => {
        if (isLast) return productsToRender
        const count = marketingTiles.filter(
            ({ position }) => (position || 0) < productsToRender.length
        ).length
        return productsToRender.slice(0, productsToRender.length - count)
    }, [productsToRender, isLast, marketingTiles])

    const isSorting = Object.keys(plpManager.plpState.sort).length > 0
    const isFiltering = Object.keys(plpManager.plpState.filters).length > 0

    const isLoadingProducts =
        (productsToRender.length === 0 || currentPageNumber === 1) &&
        isCategoryDataLoading

    // REPLACE old loading logic with helper
    const shouldDisplayLoading = useMemo(
        () =>
            shouldDisplayLoadingHelper(
                currentPageNumber,
                isSorting,
                isFiltering,
                isLoadingProducts,
                isProductsValidating,
                isGridReflowing
            ),
        [
            currentPageNumber,
            isSorting,
            isFiltering,
            isLoadingProducts,
            isProductsValidating,
            isGridReflowing
        ]
    )

    // Error de red (SWR) o página que volvió con errores GraphQL y sin productos
    const hasProductsError =
        (Boolean(productsError) || hasPageError) && !isProductsValidating

    usePLPScrollRestore({
        entry: scrollRestoreEntry,
        size: productsPageCount,
        ready:
            productsToRender.length > 0 &&
            !isCategoryDataLoading &&
            !isProductsValidating,
        scrollOffset: PLP_CONFIG.SCROLL_OFFSET
    })

    const onRetryLoadMore = useCallback(() => {
        revalidateProducts()
    }, [revalidateProducts])

    // REPLACE the old grid with the new component
    const plpGrid = useMemo(
        () => (
            <PLPProductGrid
                productsToRender={productsToRenderWithMarketingTile}
                marketingTiles={marketingTiles}
                category={category}
                shouldDisplayLoading={shouldDisplayLoading}
                gridColumns={gridColumns}
                isLast={isLast}
                onLoadMore={onLoadMoreClick}
                LoadingIndicator={LoadingIndicator}
                hasError={hasProductsError}
                onRetry={onRetryLoadMore}
                retryLabel={t('plp||LOAD MORE')}
            />
        ),
        [
            productsToRenderWithMarketingTile,
            marketingTiles,
            category,
            shouldDisplayLoading,
            gridColumns,
            isLast,
            onLoadMoreClick,
            LoadingIndicator,
            hasProductsError,
            onRetryLoadMore,
            t
        ]
    )

    // REPLACE the old slider with the new component
    const sliderComponent = useMemo(
        () => <CategorySlider sliderData={sliderData} />,
        [sliderData]
    )

    // REPLACE old analytics logic with usePLPAnalytics custom hook
    usePLPAnalytics(category, productsToRender)

    const selectedTab = useTabsControl({
        isValidating: isProductsValidating,
        tabItemSelector: '.tabbed-sections ul li'
    })

    useCarousel(isProductsValidating, {
        isMobile: true,
        selector: TAB_CONTAINER_SELECTOR,
        slideSelector: TAB_ITEMS_SELECTOR,
        arrowLeftCustomAbsoluteStyle: { bottom: '50%', left: '0' },
        arrowRightCustomAbsoluteStyle: {
            bottom: '50%',
            left: 'calc(100% - 45px)'
        }
    })

    useCarousel(isProductsValidating, {
        isMobile,
        selector: '.casaideas-carousel',
        slideSelector: '.casaideas-carousel-slide',
        arrowLeftCustomAbsoluteStyle: { bottom: '55%', left: '0' },
        arrowRightCustomAbsoluteStyle: {
            bottom: '55%',
            left: 'calc(100% - 45px)'
        },
        revalidate: selectedTab
    })

    const categoryFromProps = categoryInitialData?.categories?.items?.[0]
    const resolvedCategory = category ?? categoryFromProps

    // Cubre también el shell de resiliencia (SSR falló y se difirió al cliente):
    // loader mientras las queries client-side validan, no el NotFound.
    const isPageLoading =
        !resolvedCategory &&
        (isCategoryValidating || isProductsValidating || router.isFallback)

    if (isPageLoading) return <LoadingIndicator />
    if (!resolvedCategory && error && error.message) return <Component404 />
    if (!resolvedCategory)
        return <NotFound identifier={config.base.route?.cms_no_route!} />

    const activeCategory = resolvedCategory

    return (
        <div>
            <SEO
                preloadImages={{ images: [activeCategory.description!] }}
                category={activeCategory}
                page={PAGE_TYPE.PLP}
                products={productsToRender as ProductInterface[]}
            />
            <div className={s['main']}>
                {activeCategory.display_mode === 'PAGE' ? (
                    <RichContent
                        html={optimizeImages(activeCategory.description)}
                    />
                ) : (
                    <>
                        <div className={s['breadcrumb-wrapper']}>
                            <Breadcrumbs
                                category={{
                                    name: activeCategory.name!,
                                    breadcrumbs:
                                        activeCategory.breadcrumbs as CategoryBreadcrumb[]
                                }}
                                rootText={t('breadcrumbs|Home')}
                                separator="|"
                                // eslint-disable-next-line i18next/no-literal-string
                                urlPrefix="/categoria"
                            />
                        </div>

                        <div className={s['header']}>
                            <div className={s['title-container']}>
                                <h1 className={cn('heading-3')}>
                                    {activeCategory.name}
                                </h1>
                                <p>
                                    {totalProducts} {t('productos')}
                                </p>
                            </div>
                        </div>

                        {activeCategory.description && (
                            <div className={s['header']}>
                                <div className={s['title-container']}>
                                    <RichContent
                                        html={
                                            isGiftCardCategory
                                                ? ''
                                                : optimizeImages(
                                                      activeCategory.description
                                                  )
                                        }
                                        pageBuilderData={
                                            shouldUsePreParsedPageBuilder(
                                                activeCategory.description,
                                                initialCategoryDescription,
                                                categoryDescriptionPageBuilderData
                                            )
                                                ? categoryDescriptionPageBuilderData
                                                : undefined
                                        }
                                    />
                                </div>
                            </div>
                        )}

                        {!isGiftCardCategory && (
                            <div>{sliderData && sliderComponent}</div>
                        )}

                        {!isGiftCardCategory && (
                            <div
                                ref={filtersRef}
                                className={cn(
                                    s[
                                        !isPWA
                                            ? 'block-filter'
                                            : 'block-filter-pwa'
                                    ],
                                    { [s['filtersHidden']]: isHidden }
                                )}
                                style={
                                    isSortVisible ||
                                    plpManager.plpState.filtersVisible
                                        ? { zIndex: 10 }
                                        : {}
                                }
                            >
                                {toolbar}
                                <div className={s['mobile-filters']}>
                                    <Filters
                                        excludeFilter={['category_uid']}
                                        category={activeCategory}
                                        plpManager={plpManager}
                                        aggregations={
                                            visibleProductData?.aggregations
                                        }
                                        header={activeCategory?.name}
                                        id={
                                            decodeCategoryId(
                                                activeCategory?.uid
                                            )
                                                ? Number(
                                                      decodeCategoryId(
                                                          activeCategory?.uid
                                                      )
                                                  )
                                                : undefined
                                        }
                                        isLoading={
                                            isCategoryDataLoading ||
                                            isProductsValidating
                                        }
                                        isHorizontal={false}
                                        className={cn('animate-fade-in-right')}
                                    />
                                </div>
                            </div>
                        )}

                        <div
                            className={cn(
                                s['product-listing-page'],
                                !isHorizontalFilterMenu && s['sidebar-filters']
                            )}
                        >
                            <div>
                                {plpGrid}
                                <div className={s['load-more-wrapper']}>
                                    <p className={cn(s['viewing-count-label'])}>
                                        {t(
                                            'plp||Viewing {{renderedCount}} of {{totalCount}}',
                                            {
                                                renderedCount,
                                                totalCount: totalProducts
                                            }
                                        )}
                                    </p>
                                    <div className={s['products-amount-bar']}>
                                        <span
                                            style={{
                                                width: `${amountBarWidth}%`
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="mt-24 text-center mx-auto max-w-7xl ">
                            <div id="exponea-carousel-plp" />
                        </div>

                        <div className={s['recently-viewed']}>
                            <RecentlyViewed ProductTile={WrappedProductItem} />
                        </div>

                        <div className={s['enriched-content-block']}>
                            <RichContent
                                html={
                                    isGiftCardCategory
                                        ? activeCategory.description
                                        : activeCategory?.category_seo_description ||
                                          ''
                                }
                                pageBuilderData={
                                    shouldUsePreParsedPageBuilder(
                                        activeCategory?.category_seo_description,
                                        initialCategorySeoDescription,
                                        categorySeoFooterPageBuilderData
                                    )
                                        ? categorySeoFooterPageBuilderData
                                        : undefined
                                }
                            />
                        </div>
                    </>
                )}
            </div>
        </div>
    )
}

PLP.Layout = Layout
